/**
 * AppRoutes - Routes/Route 기반 라우팅 컴포넌트
 *
 * React Router의 Routes/Route 컴포넌트를 사용하여 라우팅 로직 일원화
 * useParams()가 정상 작동하도록 Route 컴포넌트 내에서 페이지 렌더링
 */
import { Navigate, Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { navigationEvents } from '@/lib/navigation-events';
import { hasViewModeQuery } from '@/lib/app-utils';
import { ROUTES, isCheckoutPath } from '@/config/routes';
import { QRScanner } from '@/components/QRScanner';
import { PointsRewardsPage } from '@/components/PointsRewardsPage';
import { HomeContent, OrdersContent, AdminContent } from '@/components/content';
import { PickupMenu } from '@/components/PickupMenu';
import { getValidatedTableSessionSnapshotForRoute } from '@/lib/tableContext';
import { createInitialEntrySessionTerminal } from '@/lib/sessionTerminal';
import { useSessionTermination } from '@/hooks/useSessionTermination';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/OrganizationDetailPage';
import { BrandsPage } from '@/pages/BrandsPage';
import { BrandDetailPage } from '@/pages/BrandDetailPage';
import { LoginPage } from '@/components/LoginPage';
import { PaymentModal } from '@/components/PaymentModal';
import { CompanyListPage } from '@/components/CompanyListPage';
import { ReceiptPage } from '@/components/ReceiptPage';
import { EmailVerificationPage } from '@/components/EmailVerificationPage';
import { BirthdayVerificationPage } from '@/components/BirthdayVerificationPage';
import { ProfilePage } from '@/components/ProfilePage';
import { OrderHistoryPage } from '@/components/OrderHistoryPage';
import { AnnouncementsPage } from '@/components/AnnouncementsPage';
import { PaymentMethodPage } from '@/components/PaymentMethodPage';
import { TermsOfServicePage } from '@/components/TermsOfServicePage';
import { PrivacyPolicyPage } from '@/components/PrivacyPolicyPage';
import { PartnerVisibilitySettingsPage } from '@/components/PartnerVisibilitySettingsPage';
import { LegalLandingPage } from '@/components/legal/LegalLandingPage';
import { PaymentResultPage } from '@/components/payment/PaymentResultPage';
import { CheckoutPageWithErrorBoundary } from '@/pages/CheckoutPage';
import { QRVerifyPage } from '@/pages/QRVerifyPage';
import { RestaurantPage } from '@/pages/RestaurantPage';
import { SessionExpiredPage } from '@/pages/SessionExpiredPage';
import { SessionChargePage } from '@/pages/SessionChargePage';
import { FilaVirtualPage } from '@/pages/FilaVirtualPage';
import { FacturacionPage } from '@/pages/FacturacionPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { Language } from '@/config/locales';
import type { Restaurant, SortBy, PaymentFlow, OrderHistoryItem, Company } from '@/types';
import type { Order } from '@/lib/api/types';
import type { CartItem } from '@/components/CartModal';
import type { PaymentResult } from '@/components/PaymentModal';
import type { PaymentData } from '@/contexts/CheckoutContext';
import { useLanguage } from '@/contexts/AppContext';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

/**
 * MainLayout Props - 메인 레이아웃에서 필요한 props
 */
interface MainLayoutProps {
  showPickupMenu: boolean;
  showTableOrderMenu: boolean;
  selectedRestaurant: Restaurant | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortBy;
  onSortChange: (sort: SortBy) => void;
  filteredRestaurants: Restaurant[];
  isLoading: boolean;
  hasNoNearbyStores?: boolean;
  apiError?: Error | null;
  restaurants: Restaurant[];
  onFavoriteToggle: (restaurantId: string) => void;
  onRestaurantSelect: (restaurantId: string) => void;
  onBackFromPickup: () => void;
  onBackFromTableOrder: () => void;
  onAddToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  orderedItemCount?: number;
  onPaymentClick?: () => void;
  orders: Order[];
  activeOrdersLoading: boolean;
  activeOrdersError: Error | null;
  /** #659: 인가 거절(401/403) 빈 상태 — '인증 필요' 구분 안내 */
  activeOrdersAuthRequired: boolean;
  expandedOrders: string[];
  onToggleExpand: (orderId: string) => void;
  isLoggedIn: boolean;
  /** 세션 확인 중 여부 @CODE:ISSUE-150 */
  isCheckingSession: boolean;
  onLanguageChange: (lang: Language) => void;
}

/**
 * FullPageProps - 전체 페이지 라우트에서 필요한 props
 */
interface FullPageProps {
  // Payment
  showPayment: boolean;
  onPaymentClose: () => void;
  onPaymentSuccess: (data: PaymentResult) => void;
  allCartItems: CartItem[];
  allCartTotal: number;
  showTableOrderMenu: boolean;
  // Company List
  showCompanyList: boolean;
  onCompanyListBack: () => void;
  onCompanySelect: (company: Company) => void;
  // Receipt
  paymentFlow: PaymentFlow;
  orderData: PaymentData | null;
  onReceiptBack: () => void;
  // Order History
  orderHistory: OrderHistoryItem[];
  onViewReceipt: (order: OrderHistoryItem) => void;
  // Auth
  verifiedEmail: string;
  verifiedCompany: string;
  verifiedBirthday: string;
  onEmailVerificationComplete: (email: string, company: string) => void;
  onBirthdayVerificationComplete: (birthday: string) => void;
  onLogin: (email: string, password: string) => void;
  onNavigateFromProfileToEmailVerification: () => void;
}

export interface AppRoutesProps extends MainLayoutProps, FullPageProps {
  // 추가 라우팅 관련 props
  onLocationClick: () => void;
}

/**
 * HomeWrapper - 홈 탭 콘텐츠 래퍼
 */
function HomeWrapper({
  searchQuery,
  onSearchChange,
  onLocationClick,
  sortBy,
  onSortChange,
  filteredRestaurants,
  isLoading,
  hasNoNearbyStores,
  apiError,
  onFavoriteToggle,
  onRestaurantSelect,
}: Pick<
  AppRoutesProps,
  | 'searchQuery'
  | 'onSearchChange'
  | 'onLocationClick'
  | 'sortBy'
  | 'onSortChange'
  | 'filteredRestaurants'
  | 'isLoading'
  | 'hasNoNearbyStores'
  | 'apiError'
  | 'onFavoriteToggle'
  | 'onRestaurantSelect'
>) {
  return (
    <HomeContent
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      onLocationClick={onLocationClick}
      sortBy={sortBy}
      onSortChange={onSortChange}
      filteredRestaurants={filteredRestaurants}
      isLoading={isLoading}
      hasNoNearbyStores={hasNoNearbyStores}
      apiError={apiError}
      onFavoriteToggle={onFavoriteToggle}
      onRestaurantSelect={onRestaurantSelect}
    />
  );
}

/**
 * QRWrapper - QR 스캐너 래퍼
 */
function QRWrapper() {
  return (
    <div className="flex-1 p-[min(5vw,1.25rem)] pb-[calc(5rem+2vw)]">
      <QRScanner />
    </div>
  );
}

/**
 * OrdersWrapper - 주문 목록 래퍼
 */
function OrdersWrapper({
  orders,
  activeOrdersLoading,
  activeOrdersError,
  activeOrdersAuthRequired,
  expandedOrders,
  onToggleExpand,
  restaurants,
}: Pick<
  AppRoutesProps,
  | 'orders'
  | 'activeOrdersLoading'
  | 'activeOrdersError'
  | 'activeOrdersAuthRequired'
  | 'expandedOrders'
  | 'onToggleExpand'
  | 'restaurants'
>) {
  return (
    <OrdersContent
      orders={orders}
      isLoading={activeOrdersLoading}
      error={activeOrdersError}
      authRequired={activeOrdersAuthRequired}
      expandedOrders={expandedOrders}
      onToggleExpand={onToggleExpand}
      restaurants={restaurants}
    />
  );
}

/**
 * PointsWrapper - 포인트 페이지 래퍼.
 *
 * - /points → PointsRewardsPage
 * - /points/franchise/:franchiseId → /points?brand=:franchiseId
 */
function PointsWrapper({ language: _language }: { language: Language }) {
  return (
    <div className="flex-1 pb-[calc(5rem+2vw)]">
      <Routes>
        <Route path="/" element={<PointsRoot />} />
        <Route path="/franchise/:franchiseId" element={<PointsFranchiseRedirect />} />
        <Route path="/franchise/:franchiseId/store/:storeId" element={<PointsFranchiseRedirect />} />
        <Route path="*" element={<PointsRoot />} />
      </Routes>
    </div>
  );
}

/**
 * R1 시정: PointsRewardsPage 마운트 + language 변경 시 unmount.
 */
/**
 * #434 codex R1 — `/checkout?mode=view` 직접 진입 방어 (defense in depth).
 *
 * view 모드 UI 게이트는 /restaurant 경로에서 주문 진입 자체를 막지만, 기존
 * sessionStorage 카트 + URL 직접 입력 조합은 라우트 레벨에서 차단해야 한다.
 * 앱이 생성하는 정상 checkout 링크는 mode 쿼리를 전파하지 않으므로 무회귀.
 */
function CheckoutRouteGate() {
  const location = useLocation();
  if (hasViewModeQuery(location.search)) {
    return <Navigate to="/" replace />;
  }

  return <CheckoutPageWithErrorBoundary />;
}

function PointsRoot() {
  const { language } = useLanguageHookSafe();
  // key 는 컴포넌트 instance identity 결정 — wrapper div 에 적용 (PointsRewardsPageProps 가 Record<string, never> 라 직접 props 불가)
  return (
    <div key={`points-root-${language}`}>
      <PointsRewardsPage />
    </div>
  );
}

function PointsFranchiseRedirect() {
  const { franchiseId, storeId } = useParams<{ franchiseId: string; storeId?: string }>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  if (franchiseId) {
    params.set('brand', franchiseId);
  }
  if (storeId) {
    params.set('store', storeId);
  }

  const search = params.toString();
  return <Navigate to={`/points${search ? `?${search}` : ''}`} replace />;
}

/**
 * useLanguage 훅 안전 wrapper — context 미주입 시 default fallback.
 */
function useLanguageHookSafe(): { language: Language } {
  const ctx = useLanguage();
  return { language: ctx.language };
}

/**
 * #458/#459 (Q11+Q12) 시정: RestaurantPage 강제 unmount 패턴
 *
 * - storeId 변경 (매장 전환) → 새 인스턴스 마운트 → useState/useEffect 초기화 + fetch 재발생 + scroll 0
 * - tableNum 변경 (테이블 ↔ 픽업 모드 전환) → 새 인스턴스 마운트
 *
 * key 합성: `restaurant-${id}-${tableNum ?? 'pickup'}`
 *
 * #474 R1 codex P1 시정: language 를 key 에서 제외.
 *   - 언어 전환 시 unmount 시 PickupMenu 선택 / MenuOptionModal 수량 등 로컬 state 가 유실되어 UX 끊김.
 *   - 언어 의존 fetch (useStoreMenus) 는 deps 에 locale 포함 → AppContext.locale 변경 시 자동 refetch.
 *   - i18n 텍스트는 react-i18next 가 reactive 갱신.
 *   - 따라서 unmount 없이도 매장명/메뉴명 즉시 갱신됨.
 *
 * Q11+Q12 박제 정합 — PointsRoot / BrandDetailRoute 와 동일 패턴.
 */
function RestaurantRoute({
  orderedItemCount,
  onPaymentClick,
}: Pick<AppRoutesProps, 'orderedItemCount' | 'onPaymentClick'>) {
  const { id, tableNum } = useParams<{ id: string; tableNum?: string }>();
  return (
    <RestaurantPage
      key={`restaurant-${id ?? 'unknown'}-${tableNum ?? 'pickup'}`}
      orderedItemCount={orderedItemCount}
      onPaymentClick={onPaymentClick}
    />
  );
}

/**
 * #455 PointsWrapper 진입점 — useLanguage context 주입.
 */
function PointsWrapperWithLanguage() {
  const { language } = useLanguageHookSafe();
  return <PointsWrapper language={language} />;
}

/**
 * AdminWrapper - 관리자 메뉴 래퍼
 */
function AdminWrapper({
  isLoggedIn,
  isCheckingSession,
}: Pick<AppRoutesProps, 'isLoggedIn' | 'isCheckingSession'>) {
  return (
    <AdminContent
      isLoggedIn={isLoggedIn}
      isCheckingSession={isCheckingSession}
      onProfileClick={() => navigationEvents.openProfilePage()}
      onOrderHistoryClick={() => navigationEvents.openOrderHistory()}
      onPaymentMethodClick={() => navigationEvents.openPaymentMethodPage()}
      onPrivacySettingsClick={() => navigationEvents.openPrivacySettings()}
      onAnnouncementsClick={() => navigationEvents.openAnnouncements()}
      onLoginClick={() => navigationEvents.openLoginPage()}
      onLogoutClick={() => navigationEvents.openLogoutConfirm()}
    />
  );
}

/**
 * PickupMenuWrapper - 픽업/테이블 메뉴 래퍼
 */
function PickupMenuWrapper({
  selectedRestaurant,
  showTableOrderMenu,
  onBackFromPickup,
  onBackFromTableOrder,
  onAddToCart,
  orderedItemCount,
  onPaymentClick,
  tableSessionToken,
}: Pick<
  AppRoutesProps,
  | 'selectedRestaurant'
  | 'showTableOrderMenu'
  | 'onBackFromPickup'
  | 'onBackFromTableOrder'
  | 'onAddToCart'
  | 'orderedItemCount'
  | 'onPaymentClick'
> & { tableSessionToken: string | null }) {
  const { t } = useLanguage();

  const initialEntryTerminal = useMemo(
    () => showTableOrderMenu && !tableSessionToken
      ? createInitialEntrySessionTerminal()
      : null,
    [showTableOrderMenu, tableSessionToken],
  );
  useSessionTermination(initialEntryTerminal);

  if (!selectedRestaurant) return null;
  if (showTableOrderMenu && !tableSessionToken) return null;

  const onBack = showTableOrderMenu ? onBackFromTableOrder : onBackFromPickup;
  const restaurant = {
    id: selectedRestaurant.id,
    nameKey: selectedRestaurant.nameKey,
    name:
      selectedRestaurant._name ||
      t.restaurants[selectedRestaurant.nameKey]?.name ||
      selectedRestaurant.nameKey,
    cuisine:
      selectedRestaurant._cuisine ||
      t.restaurants[selectedRestaurant.cuisineKey]?.cuisine ||
      '',
    rating: selectedRestaurant.rating,
    imageUrl: selectedRestaurant.imageUrl,
  };

  return showTableOrderMenu ? (
    <PickupMenu
      restaurant={restaurant}
      onBack={onBack}
      onAddToCart={onAddToCart}
      mode="table"
      sessionToken={tableSessionToken!}
      orderedItemCount={orderedItemCount}
      onPaymentClick={onPaymentClick}
    />
  ) : (
    <PickupMenu
      restaurant={restaurant}
      onBack={onBack}
      onAddToCart={onAddToCart}
      mode="pickup"
      orderedItemCount={orderedItemCount}
      onPaymentClick={onPaymentClick}
    />
  );
}

/**
 * AppRouteRegistry - 실제 Routes/Route 등록 테이블.
 *
 * 통합 테스트는 이 컴포넌트를 직접 렌더해 AppRoutes 의 실제 라우트 등록을 검증한다.
 */
export const AppRouteRegistry = (props: AppRoutesProps) => {
  const navigate = useNavigate();
  const { goBack } = useSafeNavigate();
  const {
    // MainLayout props
    showTableOrderMenu,
    searchQuery,
    onSearchChange,
    sortBy,
    onSortChange,
    filteredRestaurants,
    isLoading,
    hasNoNearbyStores,
    apiError,
    restaurants,
    onFavoriteToggle,
    onRestaurantSelect,
    orderedItemCount,
    onPaymentClick,
    orders,
    activeOrdersLoading,
    activeOrdersError,
    activeOrdersAuthRequired,
    expandedOrders,
    onToggleExpand,
    isLoggedIn,
    isCheckingSession,
    // FullPage props
    showPayment,
    onPaymentClose,
    onPaymentSuccess,
    allCartItems,
    allCartTotal,
    showCompanyList,
    onCompanyListBack,
    onCompanySelect,
    paymentFlow,
    orderData,
    onReceiptBack,
    orderHistory,
    onViewReceipt,
    verifiedEmail,
    verifiedCompany,
    verifiedBirthday,
    onEmailVerificationComplete,
    onBirthdayVerificationComplete,
    onLogin,
    // Additional
    onLocationClick,
  } = props;

  return (
    <Routes>
      {/* 메인 탭 라우트 */}
      <Route
        path="/"
        element={
          <HomeWrapper
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            onLocationClick={onLocationClick}
            sortBy={sortBy}
            onSortChange={onSortChange}
            filteredRestaurants={filteredRestaurants}
            isLoading={isLoading}
            hasNoNearbyStores={hasNoNearbyStores}
            apiError={apiError}
            onFavoriteToggle={onFavoriteToggle}
            onRestaurantSelect={onRestaurantSelect}
            />
        }
      />

      <Route
        path="/orders"
        element={
          <OrdersWrapper
            orders={orders}
            activeOrdersLoading={activeOrdersLoading}
            activeOrdersError={activeOrdersError}
            activeOrdersAuthRequired={activeOrdersAuthRequired}
            expandedOrders={expandedOrders}
            onToggleExpand={onToggleExpand}
            restaurants={restaurants}
          />
        }
      />

      <Route
        path="/qr"
        element={<QRWrapper />}
      />
      <Route path="/qr/:qrCode" element={<QRVerifyPage />} />
      <Route path="/session-expired" element={<SessionExpiredPage />} />
      {/* #661: 테이블 세션 전체 결제 전용 페이지 (ChargeSessionModal → 페이지 전환).
          정상 라우트 push 진입 — 뒤로가기 시 직전 화면(주문내역/메뉴) 자연 복귀.
          fail-closed: 미결제 없음/토큰 부재/빈 라인이면 페이지가 /order-history 로 redirect. */}
      <Route path="/table-session/charge" element={<SessionChargePage />} />

      {/* 줄서기(Fila Virtual) — QR 진입 고객 화면 (#781 / admin#2111 PR3).
          `?p={qrSlug}` 는 QR 배치 어트리뷰션. 자체 AppBar(매장명+15개 언어 선택)를
          가지므로 App.tsx 가 전역 헤더/하단 네비를 숨긴다. */}
      <Route path={ROUTES.WAITLIST} element={<FilaVirtualPage />} />

      <Route path="/points/*" element={<PointsWrapperWithLanguage />} />

      <Route
        path="/admin"
        element={
          <AdminWrapper
            isLoggedIn={isLoggedIn}
            isCheckingSession={isCheckingSession}
          />
        }
      />

      {/* 조직/브랜드/매장 라우트 - useParams() 사용 가능 */}
      <Route path="/organizations" element={<OrganizationsPage />} />
      <Route path="/org/:orgParam" element={<OrganizationDetailPage />} />
      <Route path="/org/:orgParam/brands" element={<BrandsPage />} />
      <Route path="/org/:orgParam/brand/:brandParam" element={<BrandDetailPage />} />

      {/* 인증 라우트 */}
      <Route
        path="/login"
        element={
          <LoginPage
            onBack={() => window.history.back()}
            onLogin={onLogin}
            onLoginComplete={() => navigate(ROUTES.HOME, { replace: true })}
          />
        }
      />
      <Route
        path="/verify/email"
        element={
          <EmailVerificationPage
            onBack={() => window.history.back()}
            onVerificationComplete={onEmailVerificationComplete}
            currentEmail={verifiedEmail}
            currentCompany={verifiedCompany}
          />
        }
      />
      <Route
        path="/verify/birthday"
        element={
          <BirthdayVerificationPage
            onBack={() => window.history.back()}
            onVerificationComplete={onBirthdayVerificationComplete}
            currentBirthday={verifiedBirthday}
          />
        }
      />

      {/* 프로필/설정 라우트 */}
      <Route
        path="/profile"
        element={
          <ProfilePage
            onBack={() => window.history.back()}
          />
        }
      />
      <Route
        path="/order-history"
        element={
          <OrderHistoryPage
            onBack={() => window.history.back()}
            orders={orderHistory}
            onViewReceipt={onViewReceipt}
          />
        }
      />
      <Route
        path="/payment-method"
        element={<PaymentMethodPage onBack={() => window.history.back()} />}
      />
      <Route
        path={ROUTES.PRIVACY_SETTINGS}
        element={
          <PartnerVisibilitySettingsPage
            onBack={() => goBack(ROUTES.ADMIN)}
          />
        }
      />

      {/* 정보 페이지 라우트 */}
      <Route
        path="/announcements"
        element={<AnnouncementsPage onBack={() => window.history.back()} />}
      />
      <Route
        path="/announcements/:id"
        element={<AnnouncementsPage onBack={() => window.history.back()} />}
      />
      <Route
        path="/legal"
        element={<LegalLandingPage onBack={() => window.history.back()} />}
      />
      <Route
        path="/terms"
        element={<TermsOfServicePage onBack={() => window.history.back()} />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPolicyPage onBack={() => window.history.back()} />}
      />

      {/* 카트 관련 라우트 — CartModal 은 AppModals 의 fixed overlay 로 별도 렌더링.
          이 Route 는 NotFoundPage catch-all 회피를 위한 빈 placeholder (#381 정합).
          background 컨텐츠는 모달 뒤로 보이지 않으므로 fragment 만 반환. */}
      <Route path="/cart" element={<></>} />

      {/* 결제 관련 라우트 */}
      <Route path={ROUTES.CHECKOUT} element={<CheckoutRouteGate />} />
      <Route path={ROUTES.RESTAURANT_TABLE_CHECKOUT} element={<CheckoutRouteGate />} />
      <Route
        path="/payment"
        element={
          showPayment ? (
            <PaymentModal
              isOpen={showPayment}
              onClose={onPaymentClose}
              onPaymentSuccess={onPaymentSuccess}
              cartItems={allCartItems}
              totalAmount={allCartTotal + (allCartTotal >= 30000 ? 0 : 3000)}
              orderNumber={
                !showTableOrderMenu
                  ? `ORD${String(Math.floor(1 + Math.random() * 999)).padStart(3, '0')}`
                  : undefined
              }
              mode={showTableOrderMenu ? 'table' : 'pickup'}
            />
          ) : null
        }
      />
      <Route
        path="/payment/result"
        element={<PaymentResultPage />}
      />
      <Route
        path="/company-list"
        element={
          showCompanyList ? (
            <CompanyListPage
              onBack={onCompanyListBack}
              onCompanySelect={onCompanySelect}
            />
          ) : null
        }
      />

      {/* 영수증 페이지 — #452: paymentFlow 게이트 제거.
          CheckoutPage 가 결제 후 /receipt/{order_id} 로 즉시 navigate 하므로
          orderData 가 채워진 상태로 진입함. orderData null fallback 은 ReceiptPage
          가 빈 데이터로 렌더 (deep-link / refresh 케이스 graceful degrade).
          paymentFlow 'success' 게이트는 레거시 주문 내역 → 영수증 진입 경로에서만 의미가
          있었지만, 이제 라우트 자체가 항상 활성. 처리중(processing) 단계에서 직접 진입은
          PaymentResultPage 가 가드. */}
      <Route
        path="/receipt/:orderId"
        element={
          <ReceiptPage
            orderData={orderData || (paymentFlow === 'success' ? {} : {})}
            onBack={onReceiptBack}
          />
        }
      />
      <Route
        path="/receipt/*"
        element={
          <ReceiptPage
            orderData={orderData || (paymentFlow === 'success' ? {} : {})}
            onBack={onReceiptBack}
          />
        }
      />

      {/* CFDI 4.0 세금계산서 발급 포털 (#877) */}
      <Route path="/facturacion" element={<FacturacionPage />} />

      {/* 매장 메뉴 페이지 (#431) — QR 픽업/테이블 navigate 진입 경로
          #458/#459 (Q11+Q12): RestaurantRoute 래퍼로 key={`${id}-${tableNum}`} 합성
          → storeId / tableNum 변경 시 React 강제 unmount → fetch 재발생 + scroll 0 + 캐시 잔존 0
          #474 R1: language 는 key 에서 제외 (locale 변경 시 카트/모달 state 보존, useStoreMenus deps 가 자동 refetch) */}
      <Route
        path="/restaurant/:id"
        element={
          <RestaurantRoute
            orderedItemCount={orderedItemCount}
            onPaymentClick={onPaymentClick}
          />
        }
      />
      <Route
        path="/restaurant/:id/table/:tableNum"
        element={
          <RestaurantRoute
            orderedItemCount={orderedItemCount}
            onPaymentClick={onPaymentClick}
          />
        }
      />

      {/* Fallback - 404 페이지 (#381 silent home fallback 차단) */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

/**
 * AppRoutes - 메인 라우팅 컴포넌트
 */
export function AppRoutes(props: AppRoutesProps) {
  const {
    showPickupMenu,
    showTableOrderMenu,
    selectedRestaurant,
    onBackFromPickup,
    onBackFromTableOrder,
    onAddToCart,
    orderedItemCount,
    onPaymentClick,
  } = props;
  const location = useLocation();
  // #683: URL-first pickup routes must keep RestaurantRoute mounted when nearby-store
  // data later toggles showPickupMenu, otherwise PickupMenu remounts and loses local state.
  const isPickupRestaurantRoute = /^\/restaurant\/[^/]+\/?$/.test(location.pathname);
  // Canonical table routes must also stay on RestaurantPage so its guarded predeploy
  // 3-key migration runs before any legacy wrapper attempts route validation.
  const isCanonicalTableRestaurantRoute =
    /^\/restaurant\/[^/]+\/table\/[^/]+\/?$/.test(location.pathname);
  const isCheckoutRoute = isCheckoutPath(location.pathname);
  const isSessionExpiredRoute = location.pathname === '/session-expired';
  const tableSessionToken = showTableOrderMenu
    ? getValidatedTableSessionSnapshotForRoute(location.pathname)?.token ?? null
    : null;

  // 픽업/테이블 메뉴가 활성화된 경우 해당 메뉴 표시
  if (
    !isPickupRestaurantRoute &&
    !isCanonicalTableRestaurantRoute &&
    !isCheckoutRoute &&
    !isSessionExpiredRoute &&
    (showPickupMenu || showTableOrderMenu) &&
    selectedRestaurant
  ) {
    return (
      <PickupMenuWrapper
        key={[
          showTableOrderMenu ? 'table' : 'pickup',
          selectedRestaurant.id,
          location.pathname,
          tableSessionToken ?? 'invalid',
        ].join(':')}
        selectedRestaurant={selectedRestaurant}
        showTableOrderMenu={showTableOrderMenu}
        onBackFromPickup={onBackFromPickup}
        onBackFromTableOrder={onBackFromTableOrder}
        onAddToCart={onAddToCart}
        orderedItemCount={orderedItemCount}
        onPaymentClick={onPaymentClick}
        tableSessionToken={tableSessionToken}
      />
    );
  }

  return <AppRouteRegistry {...props} />;
}
